import React, { useContext, useEffect, useLayoutEffect, useState, useRef } from 'react';
import { MaterialIcons } from '@expo/vector-icons';
import { ImageBackground, Platform, ScrollView, StyleSheet, Text, useWindowDimensions, View, Animated } from 'react-native';
import { TabView, SceneMap } from 'react-native-tab-view';
import { PanGestureHandler, State } from 'react-native-gesture-handler';

import { HeaderBackButton } from '@react-navigation/elements';

import { AppContext } from '~context/appContext';
import { CaseContext } from '~context/caseContext';
import { LoginRedirect } from '~utils/loginRedirect';
import { V1Request } from '~utils/apiRequest';

import BrandBackground from '~components/brandBackground';
import { WebContainer } from '~components/webContainer';
import { CaseTabBar } from '~components/caseTabBar';
import { CaseProgress } from '~components/caseProgress';
import { TopDrawer } from '~components/topDrawer';

import { Forms } from '~screens/case/forms';
import { Messages } from '~screens/case/messages';
import { Files } from './files/index';
import { Treatment } from '~screens/case/treatment';
import { Calendar } from '~screens/case/calendar';
import { GetLocalData } from '~utils/localStorage';

import { useTheme } from 'react-native-paper';

import IconForm from '@@icons/form.svg';
import IconMessage from '@@icons/message.svg';
import IconFiles from '@@icons/files.svg';
import IconTreatment from '@@icons/treatment.svg';
import IconCalendar from '@@icons/calendar.svg';

import bg_dark from '@@images/0_shared/generic_bg_dark.jpg';
import bg_light from '@@images/0_shared/generic_bg_light.jpg';

const renderScene = SceneMap({
	forms: Forms,
	messages: Messages,
	files: Files,
	treatment: Treatment,
	calendar: Calendar,
});

// eslint-disable-next-line no-unused-vars
export const Case = ({ route, navigation, }) => {
	const theme = useTheme();

	const styles = StyleSheet.create({
		backgroundImage: {
			flex: 1,
			justifyContent: 'center'
		},
		caseTitle: {
			textAlign: 'center',
			fontWeight: 'bold',
			fontSize: 18,
		},
		caseHeaderContainer: {
			display: 'flex',
			flexDirection: 'column',
			marginTop: -20,
			marginBottom: 10
		},
		webContainer: {
			backgroundColor: theme.colors.bannerBackground,
			borderRadius: 12,
			width: 'min-content',
			margin: 'auto',
			marginTop: 40,
			marginBottom: 40
		},
		webNoBannerTitle: {
			textAlign: 'center',
			fontWeight: 'bold',
			fontSize: 18,
			marginBottom: 10,
			marginTop: -20
		},
		swipeIndicator: {
			width: 30,
			height: 3,
			backgroundColor: theme.colors.primary,
			opacity: 0.5,
			alignSelf: 'center',
			marginTop: 3,
			borderRadius: 1.5,
		}
	});
	const app = useContext(AppContext);
	const _case = useContext(CaseContext);
	const layout = useWindowDimensions();
		
	const { lawsuitId, tab } = route.params;
	const [index, setIndex] = useState(tab ? tab : 0);
	const [routes, setRoutes] = useState([
		{ key: 'forms', title: 'Forms', icon: IconForm },
		{ key: 'messages', title: 'Messages', icon: IconMessage },
		{ key: 'files', title: 'Files', icon: IconFiles },
		{ key: 'treatment', title: 'Treatment', icon: IconTreatment },
		{ key: 'calendar', title: 'Calendar', icon: IconCalendar },
	]);

	const [showBanner, setShowBanner] = useState(false);
	const swipeDownProgress = useRef(new Animated.Value(0)).current;
	const [isInitialRender, setIsInitialRender] = useState(true);
	const [bg, setBg] = useState(theme.dark ? bg_dark : bg_light);
	
	const hasBanner = !!_case.bannerDetails.length;
	
	useEffect(() => {
		const fetchData = async () => {
			const localSettingsData = await GetLocalData('settingsData');
			if(!localSettingsData.MsgEnabled) setRoutes((prev) => prev.filter((x) => x.key !== 'messages'));
		};
		fetchData();
		
		// Set isInitialRender to false after a short delay
		setTimeout(() => {
			setIsInitialRender(false);
		}, 200);
	}, []);
	
	useEffect(() => {
		setBg(theme.dark ? bg_dark : bg_light);
	}, [theme]);
	
	useEffect(() => {
		// On web, params are lost if case page is refreshed, 
		// loaded from a link, or if user is navigated to it 
		// from a back button.  Need to check if params are lost
		// and get them and store them in case context
		if (tab) {
			_case.setActiveRoute(routes[tab].key);
		} else {
			_case.setActiveRoute('');
		}
		
		if (!_case.lawsuitId) {
			const loadData = async () => {
				const response = await V1Request(
					'get',
					`GetCase/${lawsuitId}`,
					null,
					`?lawsuitId=${lawsuitId}`,
					'json',
					app.setLoading
				);

				if (response.status === 401 || response.status === 403) {
					LoginRedirect(app, navigation);
					return;
				} else {
					const thisCase = response.data;
					_case.setLawsuitId(thisCase.LawsuitId);
					_case.setCaseName(thisCase.Name);
					_case.setClient(thisCase.Client);
					if (!thisCase.MessageEnabled) setRoutes((prev) => prev.filter((x) => x.key !== 'messages'));
				}

				const response2 = await V1Request(
					'get',
					'GetCaseProgress',
					null,
					`?lawsuitId=${lawsuitId}`,
					'json',
					null
				);

				_case.setCaseProgress(response2.data);
				
				const response3 = await V1Request(
					'get',
					'GetDirectBannerData',
					null,
					`?lawsuitId=${lawsuitId}`,
					'json',
					app.setLoading
				);
				_case.setBannerDetails(response3.data || []);
			};
			loadData();
		}

		// Set active tab if set
		if (tab) {
			handleIndexChanged(tab);
		}
	}, []);


	useLayoutEffect(() => {
		navigation.setOptions({
			headerLeft: () => (
				<HeaderBackButton onPress={() => navigation.replace('Cases', { fromCase: true })} />
			),
			headerTitle: () => (
				<PanGestureHandler
					onGestureEvent={(event) => {
						// Map the gesture movement to drawer opening progress
						if (hasBanner) {
							// Get translation Y limited to a reasonable range (0-100)
							const translationY = Math.min(100, Math.max(0, event.nativeEvent.translationY));
							// Map to a progress value between 0 and 1
							const progress = translationY / 100;
							swipeDownProgress.setValue(progress);
							
							// If swiped more than 70% down, fully open the drawer
							if (progress > 0.7 && !showBanner) {
								setShowBanner(true);
							}
						}
					}}
					onHandlerStateChange={(event) => {
						// For gesture completion
						const { translationY, velocityY, state } = event.nativeEvent;
						if (state === State.END && hasBanner) {
							if (translationY > 30 || velocityY > 200) {
								// Open drawer fully if swiped down enough or with enough velocity
								setShowBanner(true);
							} else {
								// Reset progress if not opened
								Animated.timing(swipeDownProgress, {
									toValue: 0,
									duration: 300,
									useNativeDriver: true
								}).start();
							}
						}
					}}
				>
					<View style={{ flexDirection: 'column', alignItems: 'center' }}>
						<View style={{ flexDirection: 'row', alignItems: 'center' }}>
							<Text 
								style={{ fontWeight: 'bold', fontSize: 18, color: theme.colors.primary }} 
								onPress={() => {
									// If drawer is visible, close it - reset swipe state too
									if (showBanner) {
										swipeDownProgress.setValue(0);
										setShowBanner(false);
									} else if (hasBanner) {
										// Only open if we have banner content
										setShowBanner(true);
									}
								}}
							>
								{_case.client}
							</Text>
							{hasBanner && <MaterialIcons
								name="info-outline"
								size={22}
								color={theme.colors.text}
								style={Platform.OS === 'web' ? { marginLeft: 8, cursor: 'pointer' } : { marginLeft: 8 }}
								onPress={() => {
									// If drawer is visible, close it - reset swipe state too
									if (showBanner) {
										swipeDownProgress.setValue(0);
										setShowBanner(false);
									} else if (hasBanner) {
										// Only open if we have banner content
										setShowBanner(true);
									}
								}}
							/>}
						</View>
						{hasBanner && <View style={styles.swipeIndicator} />}
					</View>
				</PanGestureHandler>
			)
		});
	}, [navigation, _case.client, theme.colors.primary, hasBanner, showBanner, swipeDownProgress]);

	const handleIndexChanged = (index) => {
		setIndex(index);
		_case.setActiveRoute(routes[index].key);
	};

	return <>{Platform.OS === 'web' ? WebView() : MobileView()}</>;
	function MobileView() {
		useEffect(() => {
			const loadData = async () => {
				_case.setBannerDetails([]);
				const response = await V1Request(
					'get',
					'GetDirectBannerData',
					null,
					`?lawsuitId=${lawsuitId}`,
					'json',
					app.setLoading
				);
				_case.setBannerDetails(response.data || []);
			};
			loadData();
		}, []);
		
		const renderTabBar = (props) => (
			<CaseTabBar {...props} nav={navigation} />
		);
		return (
			<ImageBackground
				resizeMode="cover"
				style={styles.backgroundImage}
				source={bg}
			>
				<TopDrawer 
					isVisible={showBanner} 
					onClose={() => {
						setShowBanner(false);
						swipeDownProgress.setValue(0);
					}} 
					bannerDetails={_case.bannerDetails}
					swipeDownProgress={swipeDownProgress}
				/>
				
				<TabView
					navigationState={{ index, routes }}
					renderScene={renderScene}
					onIndexChange={handleIndexChanged}
					renderTabBar={renderTabBar}
					initialLayout={{ width: layout.width }}
				/>
				
				<CaseProgress caseProgress={_case.caseProgress} />
			</ImageBackground>
		);
	}
	
	function WebView() {
		useEffect(() => {
			const loadData = async () => {
				_case.setBannerDetails([]);
				const response = await V1Request(
					'get',
					'GetDirectBannerData',
					null,
					`?lawsuitId=${lawsuitId}`,
					'json',
					app.setLoading
				);
				_case.setBannerDetails(response.data || []);
			};
			loadData();
		}, []);

		const renderTabBar = (props) => (
			<CaseTabBar {...props} nav={navigation} />
		);
		// Info icon is now in the header, so WebHeader matches mobile behavior
		const WebHeader = <View style={styles.caseHeaderContainer}>
			<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
				<Text 
					style={[styles.caseTitle, { color: theme.colors.primary, cursor: 'pointer' }]} 
					onClick={(e) => {
						e.stopPropagation(); // Prevent event bubbling
						// If drawer is visible, close it - reset swipe state too
						if (showBanner) {
							swipeDownProgress.setValue(0);
							setShowBanner(false);
						} else if (hasBanner) {
							// Only open if we have banner content
							setShowBanner(true);
						}
					}}
				>
					{_case.client}
				</Text>
				{hasBanner && (
					<MaterialIcons
						name="info-outline"
						size={22}
						color={theme.colors.text}
						style={{ marginLeft: 8, cursor: 'pointer' }}
						onClick={(e) => {
							e.stopPropagation(); // Prevent event bubbling
							// If drawer is visible, close it - reset swipe state too
							if (showBanner) {
								swipeDownProgress.setValue(0);
								setShowBanner(false);
							} else if (hasBanner) {
								// Only open if we have banner content
								setShowBanner(true);
							}
						}}
					/>
				)}
			</View>
			{hasBanner && <View style={styles.swipeIndicator} />}
		</View>;
		
		return (
			<BrandBackground
				onClick={() => {
					// Close drawer when clicking on the background
					if (showBanner && hasBanner) {
						setShowBanner(false);
						swipeDownProgress.setValue(0);
					}
				}}
			>
				<ScrollView
					onClick={() => {
						// Close drawer when clicking on the scroll area
						if (showBanner && hasBanner) {
							setShowBanner(false);
							swipeDownProgress.setValue(0);
						}
					}}
				>
					<View 
						style={styles.webContainer}
						onClick={(e) => {
							// Close drawer when clicking on background, but not when clicking inside the drawer content
							if (showBanner && hasBanner && e.target === e.currentTarget) {
								setShowBanner(false);
								swipeDownProgress.setValue(0);
							}
						}}
					>
						<TopDrawer 
							isVisible={showBanner && hasBanner}
							onClose={() => {
								setShowBanner(false);
								swipeDownProgress.setValue(0);
							}}
							bannerDetails={_case.bannerDetails}
							swipeDownProgress={swipeDownProgress}
							isInitialRender={isInitialRender}
						/>
						<WebContainer
							size={'md'}
							paddingBottom={0}
							paddingTop={0}
							style={{ marginTop: 0, marginBottom: 0, paddingTop: 45 }}
							onClick={() => {
								// Close drawer when clicking on the main content area
								if (showBanner && hasBanner) {
									setShowBanner(false);
									swipeDownProgress.setValue(0);
								}
							}}
						>
							{hasBanner ? WebHeader : <Text style={styles.webNoBannerTitle}>{_case.client}</Text>}
							<TabView
								navigationState={{ index, routes }}
								renderScene={renderScene}
								onIndexChange={handleIndexChanged}
								renderTabBar={renderTabBar}
								initialLayout={{ width: layout.width }}
								style={{ borderTopRightRadius: 12, borderTopLeftRadius: 12 }}
							/>
							<CaseProgress caseProgress={_case.caseProgress} />
						</WebContainer>
					</View>
				</ScrollView>
			</BrandBackground>
		);
	}
};
